import type { TipoInspecaoCasco } from "@/modules/obras/criar-obra.types";
import type { CentroCustoOpcao, TipoObraOpcao } from "@/modules/obras/obras.types";

/** Códigos dos coletores com regra própria (RN05/RN06/RN08/RN09/RN10/RN11); os demais são genéricos. */
export const TIPO_MOBILIZACAO = "100";
export const TIPO_DOCAGEM_ESPECIAL = "101";
export const TIPO_DOCAGEM_INTERMEDIARIA = "102";
export const TIPO_MANUTENCAO_CORRETIVA = "301";

/** HU 2528 (RN02/RN03/RN016): prioridade fixa por código; o primeiro presente na obra é o dominante. */
export const PRIORIDADE_DOMINANTE: readonly string[] = ["100", "101", "102", "103", "105", "301"];

export const JUSTIFICATIVA_DOMINANTE_MAX = 500;
export const JUSTIFICATIVA_DOMINANTE_OBRIGATORIA = "Informe a justificativa.";

/** RN08: 101 e 102 são excludentes na mesma obra (todos os centros de custo). */
export const TIPOS_EXCLUDENTES: readonly [string, string] = [
  TIPO_DOCAGEM_ESPECIAL,
  TIPO_DOCAGEM_INTERMEDIARIA,
];

export const INSPECAO_CASCO_LABELS: Record<TipoInspecaoCasco, string> = {
  DRY_DOCKING: "Dry Docking",
  UNDERWATER: "Underwater",
};

export const CLIENTE_MAX_LENGTH = 100;
export const CONTRATO_MAX_LENGTH = 100;

/** Tipo de obra incluído num centro de custo. Escopos macro ficam no Épico Escopo Macro. */
export interface TipoObraItem {
  /** Identidade estável (Desfazer restaura o mesmo bloco). */
  uid: string;
  tipoObraId: string;
  codigo: string;
  nome: string;
  /** RN09 (tipo 100), opcionais. */
  cliente: string;
  contrato: string;
  /** RN10 (101/102): checkbox opcional; marcado exige o tipo de inspeção. */
  haveraInspecaoCasco: boolean;
  inspecaoCasco: TipoInspecaoCasco | null;
  /** 301 inserido pela Manutenção Corretiva (etapa 1): só sai quando o toggle é desligado. */
  automatico: boolean;
}

/** Cópia do CC no momento da inclusão: o bloco e o Desfazer não dependem de nova consulta. */
export interface CentroCustoSnapshot {
  id: string;
  centroCusto: string;
  descricao: string;
  subgrupo: string;
}

export interface CentroAdicional {
  uid: string;
  centro: CentroCustoSnapshot;
  tipos: TipoObraItem[];
}

/**
 * Snapshot da Etapa 3 em `state.etapas.tiposObra` (HU 2525). Hierarquia Obra → Centro de Custo →
 * Tipos de Obra (RN01). `dominanteUid`/`justificativaDominante` guardam a HU 2528 (override
 * manual e sua justificativa); sem override, o dominante é calculado pela prioridade.
 */
export interface DadosTiposObra {
  centroCustoPrincipalId: string | null;
  /** A embarcação tem CC não bloqueado: o principal passa a ser obrigatório. */
  exigeCentroPrincipal: boolean;
  tipos: TipoObraItem[];
  centrosAdicionais: CentroAdicional[];
  /**
   * HU 2528: `uid` do tipo dominante escolhido manualmente (`null` = automático). O uid identifica
   * o par (centro, tipo): o mesmo código pode existir em dois centros. Use `tipoDominante` para
   * saber o dominante da obra (o POST exige exatamente um `dominante=true`).
   */
  dominanteUid: string | null;
  /** HU 2528: Justificativa da última alteração manual confirmada (máx. 500). */
  justificativaDominante: string;
}

export const TIPOS_OBRA_INICIAL: DadosTiposObra = {
  centroCustoPrincipalId: null,
  exigeCentroPrincipal: false,
  tipos: [],
  centrosAdicionais: [],
  dominanteUid: null,
  justificativaDominante: "",
};

export const CENTRO_PRINCIPAL_OBRIGATORIO = "Escolha o centro de custo.";
export const TIPOS_SEM_TIPO = "Adicione ao menos um tipo de obra.";
export const INSPECAO_TIPO_OBRIGATORIO = "Selecione o tipo de inspeção.";

export const chaveErroInspecao = (uid: string) => `tipo.${uid}.inspecao`;

function isTipoItem(raw: unknown): raw is TipoObraItem {
  if (typeof raw !== "object" || raw === null) return false;
  const item = raw as Record<string, unknown>;
  return typeof item.uid === "string" && typeof item.codigo === "string";
}

function isAdicional(raw: unknown): raw is CentroAdicional {
  if (typeof raw !== "object" || raw === null) return false;
  const item = raw as Record<string, unknown>;
  return (
    typeof item.uid === "string" &&
    typeof item.centro === "object" &&
    item.centro !== null &&
    Array.isArray(item.tipos)
  );
}

/** Lê o snapshot guardado (`unknown` no store); formato inesperado vira o estado vazio. */
export function readTiposObra(raw: unknown): DadosTiposObra {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return TIPOS_OBRA_INICIAL;

  const dados = raw as Partial<Record<keyof DadosTiposObra, unknown>>;

  return normalizarDominante({
    centroCustoPrincipalId:
      typeof dados.centroCustoPrincipalId === "string" ? dados.centroCustoPrincipalId : null,
    exigeCentroPrincipal: dados.exigeCentroPrincipal === true,
    tipos: Array.isArray(dados.tipos) ? dados.tipos.filter(isTipoItem) : [],
    centrosAdicionais: Array.isArray(dados.centrosAdicionais)
      ? dados.centrosAdicionais.filter(isAdicional).map((adicional) => ({
          ...adicional,
          tipos: adicional.tipos.filter(isTipoItem),
        }))
      : [],
    dominanteUid: typeof dados.dominanteUid === "string" ? dados.dominanteUid : null,
    justificativaDominante:
      typeof dados.justificativaDominante === "string" ? dados.justificativaDominante : "",
  });
}

export const isTiposObraVazio = (dados: DadosTiposObra) =>
  dados.centroCustoPrincipalId === null &&
  dados.tipos.length === 0 &&
  dados.centrosAdicionais.length === 0;

/** Há tipos ou CCs adicionais (o que a troca de embarcação descarta, RN03)? */
export const temConteudoTiposObra = (dados: DadosTiposObra) =>
  dados.tipos.length > 0 || dados.centrosAdicionais.length > 0;

/** Natureza do CC (pendência 4 da HU: classificação pelo sufixo do código e pelo subgrupo). */
export type NaturezaCentroCusto = "obra" | "manutencao-operacional" | "subsea" | "outro";

export function naturezaCentroCusto(
  centro: Pick<CentroCustoSnapshot, "centroCusto" | "subgrupo">,
): NaturezaCentroCusto {
  const codigo = centro.centroCusto.trim().toUpperCase();

  // X/Y primeiro: os CCs de obra e de manutenção operacional também vêm com subgrupo (classe da embarcação).
  if (codigo.endsWith("Y")) return "manutencao-operacional";
  if (codigo.endsWith("X")) return "obra";
  if (centro.subgrupo.trim() !== "") return "subsea";
  return "outro";
}

/** Rótulo `código - descrição` do CC (formato do Figma). */
export const rotuloCentroCusto = (
  centro: Pick<CentroCustoSnapshot, "centroCusto" | "descricao">,
) => (centro.descricao ? `${centro.centroCusto} - ${centro.descricao}` : centro.centroCusto);

/** Rótulo `código · nome` do tipo (Figma; menu, mensagens e modais do wizard). */
export const rotuloTipo = (tipo: Pick<TipoObraOpcao, "codigo" | "nome">) =>
  `${tipo.codigo} · ${tipo.nome}`;

/** Rótulo do tipo pelo código, na nomenclatura vigente do catálogo; sem catálogo, só o código. */
export function rotuloTipoPorCodigo(catalogo: readonly TipoObraOpcao[], codigo: string): string {
  const tipo = catalogo.find((item) => item.codigo === codigo);
  return tipo ? rotuloTipo(tipo) : codigo;
}

/** Sigla do subgrupo SubSea para a tag "SubSea · ABL" (ex.: "ABL SUBSEA" → "ABL"). */
export function siglaSubsea(subgrupo: string): string {
  const sigla = subgrupo
    .replace(/sub\s*sea/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return sigla || subgrupo.trim();
}

export function tagCentroAdicional(centro: CentroCustoSnapshot): string {
  return naturezaCentroCusto(centro) === "manutencao-operacional"
    ? "Manutenção Operacional"
    : `SubSea · ${siglaSubsea(centro.subgrupo)}`;
}

/**
 * Códigos de tipo que o CC aceita (RN04–RN06/RN12): Y só 301, SubSea só 100; o principal e os
 * demais aceitam todos os habilitados (`null`).
 */
export function codigosPermitidos(natureza: NaturezaCentroCusto): readonly string[] | null {
  if (natureza === "manutencao-operacional") return [TIPO_MANUTENCAO_CORRETIVA];
  if (natureza === "subsea") return [TIPO_MOBILIZACAO];
  return null;
}

export function mensagemCentroAceita(
  natureza: NaturezaCentroCusto,
  catalogo: readonly TipoObraOpcao[],
): string | null {
  const codigos = codigosPermitidos(natureza);
  if (!codigos) return null;

  const [codigo] = codigos;
  const nome = catalogo.find((tipo) => tipo.codigo === codigo)?.nome;
  return `Aceita apenas o tipo ${codigo}${nome ? ` · ${nome}` : ""}.`;
}

const todosTipos = (dados: DadosTiposObra) => [
  ...dados.tipos,
  ...dados.centrosAdicionais.flatMap((adicional) => adicional.tipos),
];

/** Primeiro código da prioridade presente em `codigos` (RN02/RN03; um único tipo é o dominante, RN04). */
export function resolverDominante(codigos: readonly string[]): string | null {
  const presentes = new Set(codigos);
  return PRIORIDADE_DOMINANTE.find((codigo) => presentes.has(codigo)) ?? codigos[0] ?? null;
}

/**
 * Tipo dominante da obra (um só bloco, mesmo com o código repetido em outro CC): o override manual
 * enquanto o tipo escolhido continua na obra; senão o de maior prioridade, e no empate de código o
 * do CC principal, depois o primeiro na ordem dos CCs. Recalcula sozinho ao incluir/remover tipo.
 */
export function tipoDominante(dados: DadosTiposObra): TipoObraItem | null {
  const tipos = todosTipos(dados);
  const manual = tipos.find((tipo) => tipo.uid === dados.dominanteUid);
  if (manual) return manual;

  const codigo = resolverDominante(tipos.map((tipo) => tipo.codigo));
  return tipos.find((tipo) => tipo.codigo === codigo) ?? null;
}

/** Override cujo tipo saiu da obra cai (Impasse 20): volta à prioridade e a justificativa se perde. */
export function normalizarDominante(dados: DadosTiposObra): DadosTiposObra {
  if (dados.dominanteUid === null) {
    return dados.justificativaDominante === "" ? dados : { ...dados, justificativaDominante: "" };
  }
  const presente = todosTipos(dados).some((tipo) => tipo.uid === dados.dominanteUid);
  return presente ? dados : { ...dados, dominanteUid: null, justificativaDominante: "" };
}

/** Confirmação do modal (RN11): guarda o novo dominante e a justificativa da última troca. */
export function alterarDominante(
  dados: DadosTiposObra,
  uid: string,
  justificativa: string,
): DadosTiposObra {
  return { ...dados, dominanteUid: uid, justificativaDominante: justificativa.trim() };
}

/** Erro da Justificativa do modal (RN10): obrigatória (sem só espaços) e de até 500 caracteres. */
export function validarJustificativaDominante(justificativa: string): string | null {
  if (justificativa.trim() === "") return JUSTIFICATIVA_DOMINANTE_OBRIGATORIA;
  if (justificativa.length > JUSTIFICATIVA_DOMINANTE_MAX) {
    return `A justificativa deve ter no máximo ${JUSTIFICATIVA_DOMINANTE_MAX} caracteres.`;
  }
  return null;
}

/** Tipos do CC principal (`centroUid` nulo) ou de um CC adicional. */
const tiposDoCentro = (dados: DadosTiposObra, centroUid: string | null) =>
  centroUid === null
    ? dados.tipos
    : (dados.centrosAdicionais.find((adicional) => adicional.uid === centroUid)?.tipos ?? []);

/**
 * Tipos que ainda podem entrar no CC (HU 2525 RN07/RN08): permitidos pelo CC, ainda não usados
 * nesse mesmo CC (em outro CC da obra o tipo se repete) e sem o excludente (101 × 102) na obra.
 */
export function tiposDisponiveis(
  catalogo: readonly TipoObraOpcao[],
  dados: DadosTiposObra,
  natureza: NaturezaCentroCusto,
  centroUid: string | null,
): TipoObraOpcao[] {
  const permitidos = codigosPermitidos(natureza);
  const usadosNoCentro = new Set(tiposDoCentro(dados, centroUid).map((tipo) => tipo.codigo));
  const usadosNaObra = new Set(todosTipos(dados).map((tipo) => tipo.codigo));

  return catalogo.filter((tipo) => {
    if (!tipo.ativo) return false;
    if (permitidos && !permitidos.includes(tipo.codigo)) return false;
    if (usadosNoCentro.has(tipo.codigo)) return false;

    const [a, b] = TIPOS_EXCLUDENTES;
    const excludente = tipo.codigo === a ? b : tipo.codigo === b ? a : null;
    return excludente === null || !usadosNaObra.has(excludente);
  });
}

let proximoUid = 0;
export const novoUidTipos = (prefixo: string) => `${prefixo}-${(proximoUid += 1)}`;

export function criarTipoItem(tipo: TipoObraOpcao, automatico = false): TipoObraItem {
  return {
    uid: novoUidTipos("tipo"),
    tipoObraId: tipo.id,
    codigo: tipo.codigo,
    nome: tipo.nome,
    cliente: "",
    contrato: "",
    haveraInspecaoCasco: false,
    inspecaoCasco: null,
    automatico,
  };
}

export const ehDocagem = (codigo: string) => TIPOS_EXCLUDENTES.includes(codigo);

/** Adiciona o tipo ao final do CC principal (`centroUid` nulo) ou de um CC adicional (RN: ao final). */
export function adicionarTipo(
  dados: DadosTiposObra,
  centroUid: string | null,
  item: TipoObraItem,
): DadosTiposObra {
  if (centroUid === null) return { ...dados, tipos: [...dados.tipos, item] };

  return {
    ...dados,
    centrosAdicionais: dados.centrosAdicionais.map((adicional) =>
      adicional.uid === centroUid ? { ...adicional, tipos: [...adicional.tipos, item] } : adicional,
    ),
  };
}

export function atualizarTipo(
  dados: DadosTiposObra,
  uid: string,
  campos: Partial<TipoObraItem>,
): DadosTiposObra {
  const aplicar = (tipos: TipoObraItem[]) =>
    tipos.map((tipo) => (tipo.uid === uid ? { ...tipo, ...campos } : tipo));

  return {
    ...dados,
    tipos: aplicar(dados.tipos),
    centrosAdicionais: dados.centrosAdicionais.map((adicional) => ({
      ...adicional,
      tipos: aplicar(adicional.tipos),
    })),
  };
}

export function removerTipo(dados: DadosTiposObra, uid: string): DadosTiposObra {
  const manter = (tipos: TipoObraItem[]) => tipos.filter((tipo) => tipo.uid !== uid);

  return {
    ...dados,
    tipos: manter(dados.tipos),
    centrosAdicionais: dados.centrosAdicionais.map((adicional) => ({
      ...adicional,
      tipos: manter(adicional.tipos),
    })),
  };
}

/** Desfazer da remoção de tipo: devolve o bloco à posição de origem (CC e índice). */
export function restaurarTipo(
  dados: DadosTiposObra,
  centroUid: string | null,
  indice: number,
  item: TipoObraItem,
): DadosTiposObra {
  if (todosTipos(dados).some((tipo) => tipo.uid === item.uid)) return dados;

  const inserir = (tipos: TipoObraItem[]) => {
    const restaurados = [...tipos];
    restaurados.splice(Math.min(indice, restaurados.length), 0, item);
    return restaurados;
  };

  if (centroUid === null) return { ...dados, tipos: inserir(dados.tipos) };

  return {
    ...dados,
    centrosAdicionais: dados.centrosAdicionais.map((adicional) =>
      adicional.uid === centroUid ? { ...adicional, tipos: inserir(adicional.tipos) } : adicional,
    ),
  };
}

/**
 * Por que o tipo não pode voltar ao CC (RN07/RN08), ou `null` se pode. Usado pelo Desfazer: entre a
 * remoção e o Desfazer o CC pode ter ganhado o mesmo tipo ou a obra o excludente.
 */
export function motivoTipoNaoRestauravel(
  dados: DadosTiposObra,
  item: Pick<TipoObraItem, "codigo" | "uid">,
  natureza: NaturezaCentroCusto,
  centroUid: string | null,
  catalogo: readonly TipoObraOpcao[],
): "repetido" | "excludente" | "nao-aceito" | null {
  const permitidos = codigosPermitidos(natureza);
  if (permitidos && !permitidos.includes(item.codigo)) return "nao-aceito";

  const fora = (tipo: TipoObraItem) => tipo.uid !== item.uid;
  const usados = new Set(
    todosTipos(dados)
      .filter(fora)
      .map((tipo) => tipo.codigo),
  );
  if (tiposDoCentro(dados, centroUid).some((tipo) => fora(tipo) && tipo.codigo === item.codigo)) {
    return "repetido";
  }

  const [a, b] = TIPOS_EXCLUDENTES;
  const excludente = item.codigo === a ? b : item.codigo === b ? a : null;
  if (excludente !== null && usados.has(excludente)) return "excludente";

  // Tipo fora do catálogo atual não é oferecido, então também não volta.
  return catalogo.some((tipo) => tipo.codigo === item.codigo) ? null : "nao-aceito";
}

export function adicionarCentro(dados: DadosTiposObra, adicional: CentroAdicional): DadosTiposObra {
  if (dados.centrosAdicionais.some((item) => item.centro.id === adicional.centro.id)) return dados;
  return { ...dados, centrosAdicionais: [...dados.centrosAdicionais, adicional] };
}

export function removerCentro(dados: DadosTiposObra, uid: string): DadosTiposObra {
  return {
    ...dados,
    centrosAdicionais: dados.centrosAdicionais.filter((adicional) => adicional.uid !== uid),
  };
}

/** Desfazer da remoção de CC: restaura o CC com tipos e dados na posição de origem. */
export function restaurarCentro(
  dados: DadosTiposObra,
  indice: number,
  adicional: CentroAdicional,
): DadosTiposObra {
  if (dados.centrosAdicionais.some((item) => item.uid === adicional.uid)) return dados;

  const restaurados = [...dados.centrosAdicionais];
  restaurados.splice(Math.min(indice, restaurados.length), 0, adicional);
  return { ...dados, centrosAdicionais: restaurados };
}

/**
 * CCs oferecidos em "Adicionar centro de custo" (RN20/RN21): só Manutenção Operacional (Y) e SubSea,
 * não bloqueados, que não sejam o principal nem já estejam adicionados.
 */
export function centrosAdicionaveis(
  centros: readonly CentroCustoOpcao[],
  dados: DadosTiposObra,
): CentroCustoOpcao[] {
  const usados = new Set(dados.centrosAdicionais.map((adicional) => adicional.centro.id));
  if (dados.centroCustoPrincipalId) usados.add(dados.centroCustoPrincipalId);

  return centros.filter((centro) => {
    if (centro.bloqueado || usados.has(centro.id)) return false;
    const natureza = naturezaCentroCusto(centro);
    return natureza === "manutencao-operacional" || natureza === "subsea";
  });
}

/** CCs oferecidos como principal: não bloqueados e que não estejam como adicionais. */
export function centrosPrincipais(
  centros: readonly CentroCustoOpcao[],
  dados: DadosTiposObra,
): CentroCustoOpcao[] {
  const adicionais = new Set(dados.centrosAdicionais.map((adicional) => adicional.centro.id));
  return centros.filter((centro) => !centro.bloqueado && !adicionais.has(centro.id));
}

/**
 * RN03: o CC de sufixo X (Obra Embarcação) vem como principal, quando existir. Sem X, o único CC
 * não bloqueado da embarcação é o principal; com vários, a escolha é do usuário.
 */
export function centroPrincipalPadrao(
  centros: readonly CentroCustoOpcao[],
): CentroCustoOpcao | null {
  const ativos = centros.filter((centro) => !centro.bloqueado);
  const obra = ativos.find((centro) => naturezaCentroCusto(centro) === "obra");
  if (obra) return obra;
  return ativos.length === 1 ? (ativos[0] ?? null) : null;
}

/**
 * Troca do CC principal: tipos que o novo principal não aceita saem, e um CC que já esteja
 * como adicional deixa de sê-lo (não pode ser os dois).
 */
export function trocarCentroPrincipal(
  dados: DadosTiposObra,
  centro: CentroCustoOpcao | null,
): DadosTiposObra {
  if (!centro) return { ...dados, centroCustoPrincipalId: null };

  const permitidos = codigosPermitidos(naturezaCentroCusto(centro));

  return {
    ...dados,
    centroCustoPrincipalId: centro.id,
    tipos: permitidos
      ? dados.tipos.filter((tipo) => permitidos.includes(tipo.codigo))
      : dados.tipos,
    centrosAdicionais: dados.centrosAdicionais.filter(
      (adicional) => adicional.centro.id !== centro.id,
    ),
  };
}

/** RN11/CA18: com a Manutenção Corretiva ativa, o CC principal precisa aceitar o tipo 301. */
export function centroAceitaCorretiva(centro: CentroCustoOpcao): boolean {
  const permitidos = codigosPermitidos(naturezaCentroCusto(centro));
  return !permitidos || permitidos.includes(TIPO_MANUTENCAO_CORRETIVA);
}

/**
 * Troca de embarcação (RN03): as seções de CC adicionais saem com tipos e dados, e o principal
 * volta a ser escolhido; os tipos do principal permanecem.
 */
export function limparPorTrocaDeEmbarcacao(dados: DadosTiposObra): DadosTiposObra {
  return {
    ...dados,
    centroCustoPrincipalId: null,
    exigeCentroPrincipal: false,
    centrosAdicionais: [],
  };
}

/** RN11: reflete a Manutenção Corretiva (etapa 1) no tipo 301, no CC principal (um 301 de outro CC não conta). */
export function sincronizarManutencaoCorretiva(
  dados: DadosTiposObra,
  ativa: boolean,
  catalogo: readonly TipoObraOpcao[],
): DadosTiposObra {
  const existe = dados.tipos.some((tipo) => tipo.codigo === TIPO_MANUTENCAO_CORRETIVA);

  if (ativa) {
    // O 301 já incluído à mão no principal é adotado (travado), sem perder escopos e dados.
    if (existe) {
      return dados.tipos.every(
        (tipo) => tipo.codigo !== TIPO_MANUTENCAO_CORRETIVA || tipo.automatico,
      )
        ? dados
        : {
            ...dados,
            tipos: dados.tipos.map((tipo) =>
              tipo.codigo === TIPO_MANUTENCAO_CORRETIVA ? { ...tipo, automatico: true } : tipo,
            ),
          };
    }

    const tipo = catalogo.find((item) => item.codigo === TIPO_MANUTENCAO_CORRETIVA && item.ativo);
    return tipo ? { ...dados, tipos: [...dados.tipos, criarTipoItem(tipo, true)] } : dados;
  }

  const automatico = todosTipos(dados).find(
    (tipo) => tipo.codigo === TIPO_MANUTENCAO_CORRETIVA && tipo.automatico,
  );
  return automatico ? removerTipo(dados, automatico.uid) : dados;
}

/** Erros da etapa (AC14): CC principal (quando há CC), ao menos um tipo e inspeção de casco coerente. */
export function validateEtapaTipos(dados: DadosTiposObra): Record<string, string> {
  const errors: Record<string, string> = {};

  if (dados.exigeCentroPrincipal && !dados.centroCustoPrincipalId) {
    errors.centroCustoPrincipalId = CENTRO_PRINCIPAL_OBRIGATORIO;
  }

  if (todosTipos(dados).length === 0) errors.tipos = TIPOS_SEM_TIPO;

  for (const tipo of todosTipos(dados)) {
    if (ehDocagem(tipo.codigo) && tipo.haveraInspecaoCasco && !tipo.inspecaoCasco) {
      errors[chaveErroInspecao(tipo.uid)] = INSPECAO_TIPO_OBRIGATORIO;
    }
  }

  return errors;
}

/**
 * Troca do CC principal com a Manutenção Corretiva em conta (RN11): o 301 continua na obra,
 * travado. `null` quando o CC não aceita o 301 — a escolha deve ser barrada.
 */
export function trocarCentroPrincipalComCorretiva(
  dados: DadosTiposObra,
  centro: CentroCustoOpcao | null,
  manutencaoCorretiva: boolean,
  catalogo: readonly TipoObraOpcao[],
): DadosTiposObra | null {
  if (manutencaoCorretiva && centro && !centroAceitaCorretiva(centro)) return null;

  return sincronizarManutencaoCorretiva(
    trocarCentroPrincipal(dados, centro),
    manutencaoCorretiva,
    catalogo,
  );
}
